import React, { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useOrgBranding } from '../contexts/OrgBrandingContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * OrgWelcomeModal
 *
 * A one-time, first-login welcome shown to members of an enterprise org whose owner
 * has set a welcome message (e.g. "Welcome to Ontario Education Online, powered by
 * Level Up Classroom!"). It shows the owner-set org logo + message on a premium,
 * branded hero so joining feels like an event. Purely additive: if the org has no
 * welcome message, nothing renders. Dismissal is remembered per user + org in
 * localStorage, so it appears once.
 */
export const OrgWelcomeModal: React.FC = () => {
  const branding = useOrgBranding();
  const { currentUser } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  const seenKey = currentUser && branding.domain
    ? `luc_welcome_seen_${currentUser.uid}_${branding.domain}`
    : null;

  const alreadySeen = (() => {
    if (!seenKey) return true; // nothing to key off → don't show
    // Fail closed: if storage is unavailable (Safari private mode, kiosks), treat as
    // seen so the welcome doesn't reappear on every page load.
    try { return localStorage.getItem(seenKey) === '1'; } catch { return true; }
  })();

  const show = !!currentUser && branding.isOrg && !!branding.welcomeMessage && !dismissed && !alreadySeen;
  if (!show) return null;

  const accent = branding.color || '#4f46e5';
  const scale = branding.logoScale && branding.logoScale > 0 ? branding.logoScale : 1;

  const close = () => {
    if (seenKey) { try { localStorage.setItem(seenKey, '1'); } catch { /* ignore */ } }
    setDismissed(true);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label={branding.name ? `Welcome to ${branding.name}` : 'Welcome'}
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-[welcomePop_.45s_cubic-bezier(.16,1,.3,1)] motion-reduce:animate-none">
        {/* Branded hero band — accent gradient with a soft radial highlight and the
            org logo floating on a white chip so any logo (colored or mono) reads. */}
        <div
          className="relative px-8 pt-10 pb-14 text-center overflow-hidden"
          style={{
            // Solid accent first as a fallback for browsers without color-mix()
            // (so white text never lands on a transparent hero); gradient overrides.
            backgroundColor: accent,
            background: `radial-gradient(120% 100% at 50% 0%, color-mix(in srgb, ${accent} 78%, white) 0%, ${accent} 55%, color-mix(in srgb, ${accent} 72%, black) 100%)`,
          }}
        >
          {/* subtle sheen */}
          <div
            className="pointer-events-none absolute -top-1/2 left-1/2 -translate-x-1/2 w-[140%] h-full opacity-40"
            style={{ background: 'radial-gradient(closest-side, rgba(255,255,255,.55), transparent)' }}
          />
          <div className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 text-white/95 text-[11px] font-bold uppercase tracking-wider mb-6 backdrop-blur-sm">
            <Sparkles size={13} className="animate-pulse motion-reduce:animate-none" />
            Welcome aboard
          </div>
          {branding.logoUrl ? (
            <div className="relative mx-auto inline-flex items-center justify-center bg-white rounded-2xl shadow-lg p-4">
              <img
                src={branding.logoUrl}
                alt={branding.name || 'Organization'}
                className="object-contain"
                style={{ height: `${Math.round(72 * scale)}px`, maxWidth: '260px' }}
              />
            </div>
          ) : (
            branding.name && (
              <h2 className="relative text-3xl font-extrabold text-white drop-shadow-sm">{branding.name}</h2>
            )
          )}
        </div>

        {/* Message + CTA, lifted over the hero with a rounded overlap. */}
        <div className="relative -mt-6 bg-white rounded-t-3xl px-8 pt-8 pb-9 text-center">
          <p className="text-xl text-gray-800 leading-relaxed whitespace-pre-line">{branding.welcomeMessage}</p>
          <button
            onClick={close}
            className="mt-8 w-full px-8 py-3.5 rounded-2xl font-bold text-white text-lg shadow-lg hover:opacity-90 active:scale-[.99] transition-all"
            style={{ backgroundColor: accent, boxShadow: `0 10px 30px -8px color-mix(in srgb, ${accent} 60%, transparent)` }}
            autoFocus
          >
            Let's get started
          </button>
        </div>
      </div>

      {/* Entrance keyframes (scoped, one-shot). Reduced-motion users get no movement. */}
      <style>{`
        @keyframes welcomePop {
          0%   { opacity: 0; transform: translateY(16px) scale(.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};
